/**
 * E-Mail-Prüfung für alle Kundenformulare.
 *
 * Hintergrund: Adressen mit Tippfehlern liefen bislang ungeprüft in den Versand.
 * Folge waren harte Bounces auf Registrierungs- und Bestätigungsmails (der Kunde
 * kommt nie in sein Konto) sowie zerrissene Bcc-Listen, bei denen die komplette
 * Nachricht verworfen wird — auch die Kopie an den Kunden.
 *
 * Geprüft wird:
 *   a) führende/anhängende Leerzeichen werden entfernt
 *   b) genau ein @, kein Leerzeichen in der Adresse
 *   c) keine zwei Punkte hintereinander
 *   d) kein Punkt am Anfang oder Ende von Local-Part und Domain
 *   e) TLD vorhanden, mindestens zwei Buchstaben
 *
 * Zusätzlich schlägt `suggestEmail` die richtige Schreibweise vor, wenn die Domain
 * ein bekannter Vertipper ist ("t-oline.de" → "t-online.de"). Die Liste stammt aus
 * echten Bounces; alle darin enthaltenen Domains haben keinen MX-Record.
 *
 * Reine Funktionen ohne Framework-Bezug — dadurch direkt per node testbar.
 */

/** Bekannte Vertipper aus den Bounce-Protokollen → richtige Domain. */
export const DOMAIN_TYPOS = {
    "t-oline.de": "t-online.de",
    "t-onlinr.de": "t-online.de",
    "t-oniele.de": "t-online.de",
    "t-online.fe": "t-online.de",
    "t.online.de": "t-online.de",
    "oline.de": "t-online.de",
    "gmail.de": "gmail.com",
    "gemail.de": "gmail.com",
    "gail.com": "gmail.com",
    "gmali.com": "gmail.com",
    "gmaul.com": "gmail.com",
    "goolemail.com": "googlemail.com",
    "wen.de": "web.de",
    "web.dd": "web.de",
    "g1mx.de": "gmx.de",
    "gnx.de": "gmx.de",
    "icloud.vom": "icloud.com",
    "me.comj": "me.com",
    "arcor.des": "arcor.de",
    "medionmail.com6": "medionmail.com",
}

/**
 * Häufige Anbieter-Domains. Dienen als Ziel für den Ähnlichkeitsvergleich und
 * zugleich als Positivliste: steht die eingegebene Domain hier drin, wird nie ein
 * Korrekturvorschlag gezeigt (sonst würde aus "gmx.at" fälschlich "gmx.de").
 */
export const KNOWN_DOMAINS = [
    "t-online.de",
    "gmail.com",
    "googlemail.com",
    "web.de",
    "gmx.de",
    "gmx.net",
    "gmx.at",
    "gmx.ch",
    "icloud.com",
    "me.com",
    "outlook.de",
    "outlook.com",
    "hotmail.com",
    "hotmail.de",
    "yahoo.de",
    "yahoo.com",
    "freenet.de",
    "arcor.de",
    "aol.com",
    "posteo.de",
    "mail.de",
    "magenta.de",
    "online.de",
]

/**
 * Real existierende Domains, die einem großen Anbieter zum Verwechseln ähnlich sehen.
 * Sie sind vom Ähnlichkeitsvergleich ausgenommen, dienen aber selbst nicht als
 * Vorschlagsziel. hmx.net hat einen gültigen MX-Record — aus "hmx.net" darf also
 * niemals "gmx.net" vorgeschlagen werden.
 */
export const IGNORED_DOMAINS = ["hmx.net"]

/** Entfernt Leerzeichen am Rand; alles andere bleibt unangetastet. */
export function normalizeEmail(value) {
    if (value === null || value === undefined) return ""
    return String(value).trim()
}

/** Prüft die Adresse gegen die Regeln a)–e). */
export function isValidEmail(value) {
    const v = normalizeEmail(value)
    if (!v) return false

    // genau ein @, keine Leerzeichen
    if (!/^[^\s@]+@[^\s@]+$/.test(v)) return false

    const [local, domain] = v.split("@")

    // keine zwei Punkte hintereinander
    if (v.includes("..")) return false

    // kein Punkt am Anfang oder Ende
    if (local.startsWith(".") || local.endsWith(".")) return false
    if (domain.startsWith(".") || domain.endsWith(".")) return false

    // TLD vorhanden, mindestens zwei Buchstaben
    if (!/\.[a-zA-Z]{2,}$/.test(domain)) return false

    return true
}

/** Liefert den Domain-Teil in Kleinschreibung, sonst einen leeren String. */
export function getDomain(value) {
    const v = normalizeEmail(value)
    const at = v.lastIndexOf("@")
    return at === -1 ? "" : v.slice(at + 1).toLowerCase()
}

/** Levenshtein-Abstand, begrenzt auf das für den Vergleich Nötige. */
export function levenshtein(a, b) {
    if (a === b) return 0
    if (!a.length) return b.length
    if (!b.length) return a.length

    let prev = Array.from({ length: b.length + 1 }, (_, i) => i)
    for (let i = 1; i <= a.length; i++) {
        const curr = [i]
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1
            curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost)
        }
        prev = curr
    }
    return prev[b.length]
}

/** Zerlegt eine Adresse in Namensteil und Domain, wenn beides brauchbar ist. */
function splitEmail(value) {
    const v = normalizeEmail(value)
    if (!v || !v.includes("@")) return null

    const domain = getDomain(v)
    if (!domain || KNOWN_DOMAINS.includes(domain) || IGNORED_DOMAINS.includes(domain)) return null

    const local = v.slice(0, v.lastIndexOf("@"))
    return local ? { local, domain } : null
}

/**
 * Belegter Vertipper aus der Liste — die Domain existiert nachweislich nicht.
 * Solche Adressen werden abgewiesen, nicht nur angemerkt.
 */
export function suggestKnownTypo(value) {
    const parts = splitEmail(value)
    if (!parts) return null

    const mapped = DOMAIN_TYPOS[parts.domain]
    return mapped ? `${parts.local}@${mapped}` : null
}

/**
 * Auffangnetz für unbekannte Vertipper: ein Zeichen Abstand zu einer häufigen
 * Anbieter-Domain. Nur ein Hinweis — hier sind Fehltreffer möglich, deshalb
 * bleibt die Eingabe erlaubt.
 */
export function suggestSimilarDomain(value) {
    const parts = splitEmail(value)
    if (!parts) return null

    // Alle Treffer sammeln statt den ersten zu nehmen: "gmx.ne" liegt einen Schritt
    // von gmx.de UND von gmx.net entfernt. Der erste Treffer haette hier gmx.de
    // vorgeschlagen, nur weil es weiter oben in der Liste steht — geraten, nicht
    // gewusst. Bei mehreren gleich nahen Kandidaten lieber gar nichts vorschlagen.
    const treffer = KNOWN_DOMAINS.filter((known) => levenshtein(parts.domain, known) === 1)
    return treffer.length === 1 ? `${parts.local}@${treffer[0]}` : null
}

/** Beide Wege zusammen — für Anzeige und Prüfung. */
export function suggestEmail(value) {
    return suggestKnownTypo(value) || suggestSimilarDomain(value)
}

/**
 * Kennzeichnet eine Fehlermeldung, hinter der ein Korrekturvorschlag steckt.
 * Das Feld macht daraus einen Text zum Anklicken; ohne die Komponente bleibt
 * eine verständliche Meldung übrig.
 */
export const TYPO_PREFIX = "Diese Adresse gibt es so nicht. Meinten Sie "

/** Weist bekannte Vertipper ab und nennt dabei die richtige Schreibweise. */
function typoRule(v) {
    const suggestion = suggestKnownTypo(v)
    return suggestion ? `${TYPO_PREFIX}${suggestion}?` : true
}

/** Vuetify-Regeln für ein Pflichtfeld mit E-Mail-Adresse. */
export const emailRules = [
    (v) => !!normalizeEmail(v) || "Bitte geben Sie eine E-Mail-Adresse ein.",
    (v) => isValidEmail(v) || "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
    typoRule,
]

/** Dieselbe Prüfung für optionale Felder — leer ist erlaubt. */
export const emailRulesOptional = [
    (v) => !normalizeEmail(v) || isValidEmail(v) || "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
    (v) => !normalizeEmail(v) || typoRule(v),
]
